import { ReactNode, useEffect, useId, useRef } from 'react';
import ReactModal from 'react-modal';
import { FiX } from 'react-icons/fi';

import Button from 'components/ui/button';
import ProgressBar from 'components/ui/progress-bar';
import { useAppInerte } from 'utils/app-inerte';
import { FOCO_SOBRE_AZUL } from 'utils/foco';
import IconeMandouBem from 'assets/img/comportamento/icon-mandou-bem.png';

export interface EtapaDoCadastro {
  titulo: string;
  descricao: string;
}

/** O que a tela mostra depois da gravação: o "Mandou bem!" do design ou o erro devolvido pela API. */
export type DesfechoDoCadastro =
  | {
      tipo: 'sucesso';
      mensagem: string;
      onAcompanhar: () => void;
      /** Texto do botão que fecha o cadastro. Padrão: "Acompanhar realização". */
      rotuloDoAcompanhar?: string;
      /** Linha extra abaixo da mensagem (a Ação ECOA mostra "Participantes desta ação: n"). */
      detalhe?: ReactNode;
    }
  | { tipo: 'erro'; titulo: string; mensagem: string };

interface ModalDeCadastroProps {
  isOpen: boolean;
  titulo: string;
  etapas: EtapaDoCadastro[];
  /** Índice (a partir de 0) da etapa em que o usuário está. */
  etapaAtual: number;
  tituloDaEtapa: string;
  lembrete?: string;
  /** Botão de fechar do canto. */
  onFechar: () => void;
  /** Esc. */
  onRequestClose: () => void;
  onAfterClose?: () => void;
  rodape?: ReactNode;
  desfecho?: DesfechoDoCadastro;
  /** Classe da área do formulário. O primeiro `div` dela é o cabeçalho da etapa, que recebe o foco na troca. */
  classeDoFormulario?: string;
  children: ReactNode;
}

/** Raiz da aplicação, escondida e inerte enquanto o modal está aberto (mesma regra do `ModalFrame`). */
const APP_ROOT_ID = 'root';

type EstadoDaEtapa = 'feita' | 'atual' | 'futura';

// SPEC_DEVIATION: o design pinta a bolinha atual de rosa com número branco (2,66:1) e a futura de branco a 22% com
// número branco (abaixo de 4,5:1). Reason: contraste (WCAG 1.4.3). A atual leva o número azul-marinho (5,3:1) e a
// futura fica só com o contorno branco sobre o azul (4,88:1).
const BOLINHA: Record<EstadoDaEtapa, string> = {
  atual: 'bg-brand-pink text-brand-navy',
  feita: 'bg-brand-yellow text-brand-blue-dark',
  futura: 'bg-transparent text-brand-white ring-1 ring-inset ring-brand-white',
};

const LEGENDA: Record<EstadoDaEtapa, string> = {
  atual: 'etapa atual',
  feita: 'etapa concluída',
  futura: 'etapa seguinte',
};

function estadoDaEtapa(indice: number, etapaAtual: number, concluido: boolean): EstadoDaEtapa {
  if (concluido || indice < etapaAtual) return 'feita';
  return indice === etapaAtual ? 'atual' : 'futura';
}

/**
 * Moldura do cadastro da Realização Destaque (RDN-30, RDN-36), a partir do modal do `Realizacao Destaque.dc.html`:
 * coluna azul com as etapas e o "Lembre-se", cabeçalho "Etapa n de 3" com a barra de progresso e o título da etapa,
 * corpo com rolagem própria e rodapé fixo. Depois da gravação, o corpo dá lugar ao desfecho ("Mandou bem!" ou erro).
 *
 * Só apresentação: quem usa entrega o conteúdo, o rodapé e os manipuladores. Foco preso e Esc vêm do react-modal.
 */
export default function ModalDeCadastro({
  isOpen,
  titulo,
  etapas,
  etapaAtual,
  tituloDaEtapa,
  lembrete,
  onFechar,
  onRequestClose,
  onAfterClose,
  rodape,
  desfecho,
  classeDoFormulario = '',
  children,
}: ModalDeCadastroProps) {
  const tituloId = useId();

  useAppInerte(isOpen);

  return (
    <ReactModal
      isOpen={isOpen}
      appElement={document.getElementById(APP_ROOT_ID) ?? undefined}
      onRequestClose={onRequestClose}
      onAfterClose={onAfterClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      aria={{ labelledby: tituloId }}
      portalClassName="relative z-50"
      overlayClassName="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(0,45,75,0.55)] p-4 lg:p-8"
      // D3: 840px e 600px do design não têm degrau no Tailwind.
      className="relative flex h-full w-full max-w-[840px] flex-col overflow-hidden rounded-jj-2xl border-4 border-brand-pink-light bg-brand-white shadow-jj-modal-brand outline-none animate-jj-pop motion-reduce:animate-none lg:h-[min(100%,600px)] lg:flex-row"
    >
      <button
        type="button"
        onClick={onFechar}
        aria-label="Fechar modal"
        // No celular o botão fica sobre a faixa azul: branco com o anel de foco branco; no desktop, sobre o branco do corpo.
        className={`absolute right-4 top-3.5 z-10 flex h-[34px] w-[34px] items-center justify-center rounded-full bg-brand-white text-brand-blue lg:bg-brand-blue lg:text-brand-yellow ${FOCO_SOBRE_AZUL} lg:focus-visible:outline-brand-blue-dark`}
      >
        <FiX size={17} strokeWidth={2.8} aria-hidden="true" />
      </button>

      <div className="flex shrink-0 flex-col gap-[22px] overflow-y-auto bg-brand-blue px-6 py-5 pr-16 lg:w-[246px] lg:py-7 lg:pr-6">
        <h2 id={tituloId} className="m-0 font-display text-xl font-extrabold leading-tight text-brand-yellow">
          {titulo}
        </h2>

        <ol aria-label="Etapas do cadastro" className="m-0 hidden list-none flex-col gap-3.5 p-0 lg:flex">
          {etapas.map((etapa, indice) => {
            const estado = estadoDaEtapa(indice, etapaAtual, !!desfecho);

            return (
              <li key={etapa.titulo} aria-current={estado === 'atual' ? 'step' : undefined} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${BOLINHA[estado]}`}
                >
                  {indice + 1}
                </span>
                <span className="flex flex-col">
                  <span className="font-display text-[13px] font-bold text-brand-white">
                    {etapa.titulo}
                    <span className="sr-only"> ({LEGENDA[estado]})</span>
                  </span>
                  {/* SPEC_DEVIATION: o design usa branco a 75% (abaixo de 4,5:1 sobre o azul). Reason: contraste; branco pleno dá 4,88:1. */}
                  <span className="text-jj-micro text-brand-white">{etapa.descricao}</span>
                </span>
              </li>
            );
          })}
        </ol>

        {lembrete && (
          // SPEC_DEVIATION: o design põe o "Lembre-se" amarelo pequeno sobre branco a 12% em cima do azul.
          // Reason: texto amarelo pequeno sobre `brand-blue` vai a `brand-blue-dark` (regra do Feed e da Minha Equipe).
          <div className="mt-auto hidden rounded-jj-lg bg-brand-blue-dark p-3.5 lg:block">
            <p className="m-0 mb-1.5 text-jj-micro font-bold uppercase tracking-[0.05em] text-brand-yellow">Lembre-se</p>
            <p className="m-0 text-jj-label leading-normal text-brand-white">{lembrete}</p>
          </div>
        )}
      </div>

      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {desfecho ? (
          <Desfecho desfecho={desfecho} />
        ) : (
          <>
            <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-4 pt-6 lg:px-8 lg:pt-[30px] ${classeDoFormulario}`}>
              <div className="mb-[18px] pr-11">
                <div className="mb-[18px] flex items-center gap-2.5">
                  {/* D3: o design usa 9px; vai ao menor degrau do projeto (fs-micro, 10px). */}
                  <span className="shrink-0 rounded-jj-pill bg-brand-pink-light px-2.5 py-1 text-jj-micro font-bold uppercase tracking-[0.05em] text-brand-navy">
                    Etapa {etapaAtual + 1} de {etapas.length}
                  </span>
                  <ProgressBar
                    value={((etapaAtual + 1) / etapas.length) * 100}
                    label="Progresso do cadastro"
                    className="!h-1.5"
                  />
                </div>
                <h3 tabIndex={-1} className="m-0 font-display text-[19px] font-bold text-brand-blue-dark outline-none">
                  {tituloDaEtapa}
                </h3>
              </div>

              {children}
            </div>

            {rodape && (
              <footer className="flex shrink-0 flex-wrap items-center gap-3.5 border-t border-surface-200 bg-brand-white px-5 py-4 lg:min-h-[86px] lg:px-8">
                {rodape}
              </footer>
            )}
          </>
        )}
      </div>
    </ReactModal>
  );
}

function Desfecho({ desfecho }: { desfecho: DesfechoDoCadastro }) {
  // O rodapé com o botão focado some ao trocar pelo desfecho: o foco vai ao título dele (WCAG 2.4.3 e 4.1.3).
  const titulo = useRef<HTMLHeadingElement>(null);
  useEffect(() => titulo.current?.focus(), []);

  return (
    <div role="status" className="flex flex-1 flex-col items-center justify-center gap-3.5 overflow-y-auto px-8 py-7 text-center">
      {desfecho.tipo === 'sucesso' ? (
        <>
          <img src={IconeMandouBem} alt="" className="block h-auto w-[92px]" />
          <h3 ref={titulo} tabIndex={-1} className="m-0 font-display text-[26px] font-extrabold text-brand-blue-dark outline-none">
            Mandou bem!
          </h3>
          <p className="m-0 max-w-[380px] text-jj-body-sm leading-relaxed text-content-soft">{desfecho.mensagem}</p>
          {desfecho.detalhe}
          <Button variant="solido-destaque" onClick={desfecho.onAcompanhar} className="mt-1.5">
            {desfecho.rotuloDoAcompanhar ?? 'Acompanhar realização'}
          </Button>
        </>
      ) : (
        <>
          <h3 ref={titulo} tabIndex={-1} className="m-0 font-display text-[22px] font-extrabold text-brand-blue-dark outline-none">
            {desfecho.titulo}
          </h3>
          <p className="m-0 max-w-[380px] text-jj-body-sm leading-relaxed text-content">{desfecho.mensagem}</p>
        </>
      )}
    </div>
  );
}
